import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
} from "react";

/* ========================================================================== */
/* Types                                                                      */
/* ========================================================================== */

export type Track = {
  id: string;
  name: string;
  kind: "video" | "audio" | "npc";
  muted?: boolean;
  solo?: boolean;
  locked?: boolean;
  height?: number;
  collapsed?: boolean;
};

export type RampCurve =
  | "linear"
  | "easeIn"
  | "easeOut"
  | "easeInOut"
  | "expo"
  | "log"
  | "sCurve"
  | "cinematic";

export type NPCSource = "sdne" | "npc" | "user" | "system";
export type NPCEmotion =
  | "calm"
  | "focus"
  | "shadow"
  | "anxious"
  | "confident"
  | "curious"
  | "supportive"
  | "challenging"
  | string;

export type NPCIntent =
  | "guide"
  | "warn"
  | "challenge"
  | "encourage"
  | "reflect"
  | "ask"
  | "narrate"
  | "inform"
  | "reframe"
  | string;

export type NPCSuggestion = {
  kind: "insert" | "replace" | "tweak" | "annotate";
  text?: string;
  start?: number;
  end?: number;
  confidence?: number;
  tags?: string[];
};

export type NPCProvenance = {
  model?: string;
  version?: string;
  promptHash?: string;
  sourceId?: string;
};

export type NPCMeta = {
  id?: string;
  name?: string;
  persona?: string;
  traits?: string[];
  intent?: NPCIntent;
  emotion?: NPCEmotion;
  confidence?: number;
  memoryTags?: string[];
  memoryRefs?: string[];
  suggestion?: NPCSuggestion;
  action?: string;
  source?: NPCSource;
  provenance?: NPCProvenance;
  createdAt?: number;
  updatedAt?: number;
};

export type ClipFX = {
  gainDb?: number;
  fadeIn?: number;
  fadeOut?: number;
  curveIn?: RampCurve;
  curveOut?: RampCurve;
  duck?: {
    regions?: Array<{
      start: number;
      end: number;
      gainDb?: number;
      attack?: number;
      release?: number;
    }>;
  };
};

export type Clip = {
  id: string;
  trackId: string;
  start: number;
  dur: number;
  label: string;
  groupId?: string;
  fx?: ClipFX;
  npc?: NPCMeta;
  probs?: Record<string, number>;
};

export type Marker = {
  id: string;
  t: number;
  label: string;
};

export type Overlays = {
  captions: boolean;
  safe: boolean;
  overlays: boolean;
};

export type Settings = {
  fps: 24 | 25 | 30 | 50 | 60;
  aspect: "16:9" | "9:16" | "1:1";
  background: "Black" | "White" | "Transparent";
};

export type EmotionalPreset =
  | "gentle-fade"
  | "urgent-cut"
  | "cinematic-swell";

/* ========================================================================== */
/* UI State                                                                  */
/* ========================================================================== */

export type UIState = {
  cursor: number;
  zoom: number;
  snap: boolean;
  overlays: Overlays;
  selectionIds: string[];
  aiEnabled: boolean;

  previewTimeSec?: number | null;
  previewPreset?: { name: EmotionalPreset; scope: "selection" | "all" };
  previewRange?: { start: number; end: number } | null;
};

export type Viewport = {
  startSec: number;
  endSec: number;
};

export type StateSnapshot = {
  tracks: Track[];
  clips: Clip[];
  markers: Marker[];
  settings: Settings;
  ui: UIState;
  playheadSec: number;
  viewport: Viewport;
};

/* ========================================================================== */
/* Canonical State                                                           */
/* ========================================================================== */

export type State = StateSnapshot & {
  _past: StateSnapshot[];
  _future: StateSnapshot[];
  _lastTransactionId?: string;
};

/* ========================================================================== */
/* Defaults                                                                  */
/* ========================================================================== */

const DEFAULT_TRACKS: Track[] = [
  { id: "v1", name: "Video 1", kind: "video" },
  { id: "a1", name: "Audio 1", kind: "audio" },
  { id: "a2", name: "Audio 2", kind: "audio" },
];

const DEFAULT_STATE: StateSnapshot = {
  tracks: DEFAULT_TRACKS,
  clips: [],
  markers: [],
  settings: {
    fps: 30,
    aspect: "16:9",
    background: "Black",
  },
  ui: {
    cursor: 0,
    zoom: 1,
    snap: true,
    overlays: { captions: true, safe: true, overlays: true },
    selectionIds: [],
    aiEnabled: false,
    previewTimeSec: null,
  },
  playheadSec: 0,
  viewport: { startSec: 0, endSec: 10 },
};

const MAX_HISTORY = 50;

/* ========================================================================== */
/* Snapshot Utility                                                          */
/* ========================================================================== */

function snapshot(state: State): StateSnapshot {
  const { _past, _future, _lastTransactionId, ...rest } = state;
  return {
    ...rest,
    tracks: rest.tracks.map(t => ({ ...t })),
    clips: rest.clips.map(c => ({ ...c })),
    markers: rest.markers.map(m => ({ ...m })),
    settings: { ...rest.settings },
    ui: { ...rest.ui, selectionIds: [...rest.ui.selectionIds] },
    viewport: { ...rest.viewport },
  };
}

/* ========================================================================== */
/* History Wrapper                                                           */
/* ========================================================================== */

function wrapWithHistory(
  prev: State,
  next: StateSnapshot,
  transactionId?: string
): State {
  const sameTx =
    transactionId &&
    prev._lastTransactionId &&
    transactionId === prev._lastTransactionId;

  if (sameTx) {
    return {
      ...next,
      _past: prev._past,
      _future: prev._future,
      _lastTransactionId: transactionId,
    };
  }

  const past = [...prev._past, snapshot(prev)];
  if (past.length > MAX_HISTORY) past.shift();

  return {
    ...next,
    _past: past,
    _future: [],
    _lastTransactionId: transactionId,
  };
}

/* ========================================================================== */
/* Actions                                                                   */
/* ========================================================================== */

type Meta = { transactionId?: string };

type Action =
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "SET_VIEWPORT"; startSec: number; endSec: number; meta?: Meta }
  | { type: "SPLIT_AT_PLAYHEAD"; meta: Meta }
  | { type: "SET_CURSOR"; cursor: number; meta?: Meta }
  | { type: "REMOVE_MARKER"; markerId: string; meta?: Meta }
  | { type: "UPDATE_CLIP"; clipId: string; patch: Partial<Clip>; meta?: Meta }
  | {
      type: "SET_CLIP_RAMP";
      clipId: string;
      patch: Partial<Pick<ClipFX, "fadeIn" | "fadeOut" | "curveIn" | "curveOut">>;
      transient?: boolean;
      meta?: Meta;
    }
  | {
      type: "SET_PREVIEW_PRESET";
      preset: { name: EmotionalPreset; scope: "selection" | "all" };
      meta?: Meta;
    }
  | { type: "CLEAR_PREVIEW_PRESET"; meta?: Meta }
  | {
      type: "APPLY_EMOTIONAL_PRESET";
      preset: EmotionalPreset;
      scope: "selection" | "all";
      meta?: Meta;
    };

function emotionalPresetPatch(preset: EmotionalPreset): Partial<ClipFX> {
  switch (preset) {
    case "gentle-fade":
      return { fadeIn: 0.45, fadeOut: 0.45, curveIn: "easeInOut", curveOut: "easeInOut" };
    case "urgent-cut":
      return { fadeIn: 0.08, fadeOut: 0.08, curveIn: "easeOut", curveOut: "easeIn" };
    case "cinematic-swell":
      return { fadeIn: 0.8, fadeOut: 0.9, curveIn: "cinematic", curveOut: "cinematic" };
    default:
      return {};
  }
}

/* ========================================================================== */
/* Reducer                                                                  */
/* ========================================================================== */

function reducer(state: State, action: Action): State {
  const tx = "meta" in action ? action.meta?.transactionId : undefined;

  switch (action.type) {
    /* ---------------------------------------------------------------------- */
    /* Viewport (canonical, time-domain only)                                  */
    /* ---------------------------------------------------------------------- */
    case "SET_VIEWPORT": {
      const start = Math.max(0, action.startSec);
      const end = Math.max(start + 0.001, action.endSec);

      return {
        ...state,
        viewport: { startSec: start, endSec: end },
      };
    }

    case "SET_CURSOR": {
      const cursor = Math.max(0, action.cursor);
      return {
        ...state,
        playheadSec: cursor,
        ui: {
          ...state.ui,
          cursor,
        },
      };
    }

    case "REMOVE_MARKER": {
      const next: StateSnapshot = {
        ...state,
        markers: state.markers.filter((marker) => marker.id !== action.markerId),
      };
      return wrapWithHistory(state, next, tx);
    }

    case "UPDATE_CLIP": {
      const next: StateSnapshot = {
        ...state,
        clips: state.clips.map((clip) =>
          clip.id === action.clipId
            ? {
                ...clip,
                ...action.patch,
                fx: action.patch.fx ? { ...(clip.fx ?? {}), ...action.patch.fx } : clip.fx,
              }
            : clip,
        ),
      };
      return wrapWithHistory(state, next, tx);
    }

    case "SET_CLIP_RAMP": {
      const next: StateSnapshot = {
        ...state,
        clips: state.clips.map((clip) =>
          clip.id === action.clipId
            ? {
                ...clip,
                fx: {
                  ...(clip.fx ?? {}),
                  ...action.patch,
                },
              }
            : clip,
        ),
      };
      return action.transient ? { ...state, ...next } : wrapWithHistory(state, next, tx);
    }

    case "SET_PREVIEW_PRESET": {
      return {
        ...state,
        ui: {
          ...state.ui,
          previewPreset: action.preset,
        },
      };
    }

    case "CLEAR_PREVIEW_PRESET": {
      return {
        ...state,
        ui: {
          ...state.ui,
          previewPreset: undefined,
        },
      };
    }

    case "APPLY_EMOTIONAL_PRESET": {
      const patch = emotionalPresetPatch(action.preset);
      const selection = new Set(state.ui.selectionIds);
      const shouldApply = (clip: Clip) => action.scope === "all" || selection.has(clip.id);
      const next: StateSnapshot = {
        ...state,
        clips: state.clips.map((clip) =>
          shouldApply(clip)
            ? {
                ...clip,
                fx: {
                  ...(clip.fx ?? {}),
                  ...patch,
                },
              }
            : clip,
        ),
        ui: {
          ...state.ui,
          previewPreset: undefined,
        },
      };
      return wrapWithHistory(state, next, tx);
    }

    /* ---------------------------------------------------------------------- */
    /* Atomic clip split                                                       */
    /* ---------------------------------------------------------------------- */
    case "SPLIT_AT_PLAYHEAD": {
      const t = state.playheadSec;

      const idx = state.clips.findIndex(
        c => t > c.start && t < c.start + c.dur
      );
      if (idx === -1) return state;

      const clip = state.clips[idx];
      if (t <= clip.start || t >= clip.start + clip.dur) return state;

      const left: Clip = {
        ...clip,
        id: crypto.randomUUID(),
        dur: t - clip.start,
      };

      const right: Clip = {
        ...clip,
        id: crypto.randomUUID(),
        start: t,
        dur: clip.start + clip.dur - t,
      };

      const next: StateSnapshot = {
        ...state,
        clips: [
          ...state.clips.slice(0, idx),
          left,
          right,
          ...state.clips.slice(idx + 1),
        ],
        ui: {
          ...state.ui,
          selectionIds: [left.id, right.id],
        },
      };

      return wrapWithHistory(state, next, tx);
    }

    default:
      return state;
  }
}

/* ========================================================================== */
/* Context                                                                  */
/* ========================================================================== */

const OmniCineContext = createContext<{
  state: State;
  dispatch: React.Dispatch<Action>;
} | null>(null);

function makeInitialState(
  initial?: Partial<StateSnapshot>
): State {
  return {
    ...DEFAULT_STATE,
    ...(initial ?? {}),
    _past: [],
    _future: [],
    _lastTransactionId: undefined,
  };
}

export function OmniCineProvider({
  initial,
  children,
}: {
  initial?: Partial<StateSnapshot>;
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(
    reducer,
    makeInitialState(initial)
  );

  const value = useMemo(
    () => ({ state, dispatch }),
    [state]
  );

  return (
    <OmniCineContext.Provider value={value}>
      {children}
    </OmniCineContext.Provider>
  );
}

export function useOmniCine() {
  const ctx = useContext(OmniCineContext);
  if (!ctx) {
    throw new Error("useOmniCine must be used within OmniCineProvider");
  }
  return ctx;
}

/* ========================================================================== */
/* Action Creators                                                           */
/* ========================================================================== */

export const actions = {
  setViewport(
    startSec: number,
    endSec: number,
    transactionId?: string
  ): Action {
    return {
      type: "SET_VIEWPORT",
      startSec,
      endSec,
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  splitAtPlayhead(transactionId: string): Action {
    return {
      type: "SPLIT_AT_PLAYHEAD",
      meta: { transactionId },
    };
  },

  setCursor(cursor: number, transactionId?: string): Action {
    return {
      type: "SET_CURSOR",
      cursor,
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  removeMarker(markerId: string, transactionId?: string): Action {
    return {
      type: "REMOVE_MARKER",
      markerId,
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  updateClip(clipId: string, patch: Partial<Clip>, transactionId?: string): Action {
    return {
      type: "UPDATE_CLIP",
      clipId,
      patch,
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  setClipRamp(
    clipId: string,
    patch: Partial<Pick<ClipFX, "fadeIn" | "fadeOut" | "curveIn" | "curveOut">>,
    transient: boolean = false,
    transactionId?: string,
  ): Action {
    return {
      type: "SET_CLIP_RAMP",
      clipId,
      patch,
      transient,
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  setPreviewPreset(preset: { name: EmotionalPreset; scope: "selection" | "all" }, transactionId?: string): Action {
    return {
      type: "SET_PREVIEW_PRESET",
      preset,
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  clearPreviewPreset(transactionId?: string): Action {
    return {
      type: "CLEAR_PREVIEW_PRESET",
      meta: transactionId ? { transactionId } : undefined,
    };
  },

  applyEmotionalPreset(preset: EmotionalPreset, scope: "selection" | "all", transactionId?: string): Action {
    return {
      type: "APPLY_EMOTIONAL_PRESET",
      preset,
      scope,
      meta: transactionId ? { transactionId } : undefined,
    };
  },
};
